import React, { useEffect, useState, useMemo } from 'react';
import Container from 'react-bootstrap/Container';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import moment from 'moment';

import {
    GeneralDialog,
    GeneralInput,
    GeneralSelect,
    LoadingStatusWrapper,
    TimeSelectorRow
} from 'sharedComponents';
import ErrorView from 'sharedComponents/ErrorBoundary/ErrorView';

import './style.scss';
import { fleetDetailedInstantReportTypes, fleetReportTypes, instantReportTypes, PAYLOAD_DATE_FORMAT, reportTypes, unitTypesForReport } from '../constants';
import { assetType, apiCallStatus } from 'helpers/constants';
import { ReportGeneratorNotification } from './ReportGeneratorNotification';
import { isReportMetricOnly, isReportTypeFleet, isReportTypeFleetDetailedInstant } from './helpers';
import { getPreselectedDates } from '../helpers';
import { minimumRequiredDaysRange, numberOfMonthsInView, maximumDaysRange, DATE_RANGE_ERROR_TOO_SHORT, DATE_RANGE_ERROR_TOO_LONG, DATE_RANGE_ERROR_OUTSIDE_RAW_DATA } from './constants';
import { IconClose, IconEllipsis } from 'svgIcons/MotionPortalIcons';
import RawDataCollectionDialog from './RawDataCollectionDialog';
import { getNrOfDaysBetween } from 'helpers/dateHelper';
import { formatDateTime } from 'helpers/dateTimeFormatter';
import { AssetsSelectionDialog } from './AssetsSelectionDialog';

const validateDateRange = (from, to, rawDataCollectionDate) => {
    if (!from || !to) { return { isValid: false, error: null }; }
    const daysDiff = getNrOfDaysBetween(new Date(from), new Date(to));
    if (daysDiff < minimumRequiredDaysRange) {
        return { isValid: false, error: DATE_RANGE_ERROR_TOO_SHORT };
    }
    if (daysDiff > maximumDaysRange) {
        return { isValid: false, error: DATE_RANGE_ERROR_TOO_LONG };
    }
    if (rawDataCollectionDate && !moment(rawDataCollectionDate).isBetween(moment.utc(from).startOf('day'), moment.utc(to).endOf('day'), null, '[]')) {
        return { isValid: false, error: DATE_RANGE_ERROR_OUTSIDE_RAW_DATA };
    }
    return { isValid: true, error: null };
};

export const ReportGeneratorComponent = ({
    model: {
        isModalOpen,
        capabilitiesLoadingStatuses,
        assetOptions,
        preselectedAsset
    }, actions: {
        setReportTypeInRedux,
        // getCapabilitiesByDataSourceType,
        generateReport,
        generateFleetReport,
        generateFleetDetailedReport,
        closeGeneratorModal,
        getSensorOfAssets,
        getRawDataCollectionTimestamps
    },
    rawDataCollections
}) => {
    const { t: translate } = useTranslation();
    const initialReportType = instantReportTypes[0];
    const initialUnitType = unitTypesForReport[0];
    const initialPeriod = getPreselectedDates(7);

    const getReportTypeForAsset = (asset) => {
        if (asset?.type === assetType.MOTOR) {
            return instantReportTypes.find(t => t.reportTypeId === reportTypes.InstantAssetMotor);
        }
        if (asset?.type === assetType.DRIVE) {
            return instantReportTypes.find(t => t.reportTypeId === reportTypes.Drive);
        }
        return null;
    };

    const availableReportTypes = useMemo(() => {
        if (preselectedAsset) {
            const reportType = getReportTypeForAsset(preselectedAsset);
            return reportType ? [reportType] : instantReportTypes;
        }
        return [...instantReportTypes, ...fleetReportTypes, ...fleetDetailedInstantReportTypes];
    }, [preselectedAsset]);

    const [selectedReportType, setSelectedReportType] = useState(initialReportType);
    const [selectedAsset, setSelectedAsset] = useState(null);
    const [selectedUnit, setSelectedUnit] = useState(initialUnitType);

    const [isFetchingStarted, setIsFetchingStarted] = useState(false);
    const [fleetMode, setFleetMode] = useState(false);
    const [fleetDetailedMode, setFleetDetailedMode] = useState(false);
    const [reportingPeriod, setReportingPeriod] = useState(initialPeriod);
    const [acceptButtonEnabled, setAcceptButtonEnabled] = useState(false);
    const [isShowRawDataCollectionDialog, setShowRawDataCollectionDialog] = useState(false);
    const [isShowSelectedAssetsDialog, setIsShowSelectedAssetsDialog] = useState(false);
    const [selectedRawDataCollection, setSelectedRawDataCollection] = useState(null);
    const [selectedTimestamp, setSelectedTimestamp] = useState(null);
    const [selectedAssetsIdsForFleetDetailed, setSelectedAssetsIdsForFleetDetailed] = useState([]);
    const [selectedAssetCountForFleetDetailed, setSelectedAssetCountForFleetDetailed] = useState('');
    const [generationError, setGenerationError] = useState(null);

    const reportGenerationLoadingStatus = useSelector(({ reports }) => reports?.reportGenerator?.loadingStatus);
    const reportGenerationError = useSelector(({ reports }) => reports?.reportGenerator?.error);
    const [dateRangeValidation, setDateRangeValidation] = useState({ isValid: true, error: null });
    const isDateRangeValid = dateRangeValidation.isValid;

    const isMetricOnly = useMemo(() => isReportMetricOnly(selectedReportType), [selectedReportType]);

    useEffect(() => {
        const validation = validateDateRange(reportingPeriod.from, reportingPeriod.to, selectedRawDataCollection);
        setDateRangeValidation(validation);
    }, [reportingPeriod, selectedRawDataCollection]);

    useEffect(() => {
        const fleetMode = isReportTypeFleet(selectedReportType?.reportTypeId);
        const fleetDetailedMode = isReportTypeFleetDetailedInstant(selectedReportType?.reportTypeId);
        setFleetMode(fleetMode);
        setFleetDetailedMode(fleetDetailedMode);
        setReportTypeInRedux(selectedReportType);
        setSelectedAsset(preselectedAsset || null);
        setSelectedRawDataCollection(null);
        setSelectedAssetsIdsForFleetDetailed([]);
        setSelectedAssetCountForFleetDetailed('');

        if (isMetricOnly) {
            setSelectedUnit(unitTypesForReport[0]);
        }

        if (isDateRangeValid) {
            setAcceptButtonEnabled(fleetMode || fleetDetailedMode);
        } else {
            setAcceptButtonEnabled(false);
        }
        // Only reset fields when the report type itself changes, not on every date-range validity flip
    }, [selectedReportType]);


    useEffect(() => {
        //Get Capabilities for the initially selected ReportType
        // getCapabilitiesByDataSourceType(initialReportType);
        getSensorOfAssets();
    }, []);

    useEffect(() => {
        if (isModalOpen) {
            setSelectedAsset(preselectedAsset || null);
            setGenerationError(null);
            setReportingPeriod(initialPeriod); // Only reset to 7 days on first open
            if (preselectedAsset) {
                const reportType = getReportTypeForAsset(preselectedAsset);
                setSelectedReportType(reportType || initialReportType);
            }
        }
    }, [isModalOpen]);

    useEffect(() => {
        //Get Capabilities for the non initial report Types
        if (isModalOpen && !isFetchingStarted) {
            // const otherReportTypes = instantReportTypes.filter((type) => type !== initialReportType);
            // otherReportTypes.forEach((type) => {
            // getCapabilitiesByDataSourceType(type);
            // });
            setIsFetchingStarted(true);
        }
    }, [isModalOpen]);

    const cleanup = () => {
        setSelectedReportType(initialReportType);
        setSelectedAsset(null);
        setSelectedUnit(initialUnitType);
        setSelectedRawDataCollection(null);
        closeGeneratorModal();
        setSelectedTimestamp(null);
        setSelectedAssetCountForFleetDetailed('');
        setSelectedAssetsIdsForFleetDetailed([]);
    };

    useEffect(() => {
        setAcceptButtonEnabled(!!selectedAsset || isDateRangeValid);
    }, [selectedAsset, dateRangeValidation]);

    useEffect(() => {
        if (!dateRangeValidation.isValid) {
            setAcceptButtonEnabled(false);
        } else if (fleetMode) {
            setAcceptButtonEnabled(true);
        } else if (fleetDetailedMode) {
            setAcceptButtonEnabled(selectedAssetsIdsForFleetDetailed.length > 0);
        } else {
            setAcceptButtonEnabled(!!selectedAsset);
        }
    }, [selectedAsset, selectedAssetsIdsForFleetDetailed, fleetMode, fleetDetailedMode, dateRangeValidation]);

    useEffect(() => {
        if (reportGenerationLoadingStatus === apiCallStatus.ERROR) {
            setGenerationError({
                code: reportGenerationError?.status || reportGenerationError?.statusCode || 500,
                message: reportGenerationError?.message || translate('ABB.Powertrain.Frontend.reportGenerationFailedMessage')
            });
        }
    }, [reportGenerationLoadingStatus]);

    const checkFutureDate = (range) => {
        if (!range?.to || !range?.from) { return initialPeriod; }

        // Convert provided dates to UTC and check against current UTC time.
        const utcFrom = moment.utc(range.from);
        const utcTo = moment.utc(range.to);
        const utcNow = moment.utc();

        if (!utcFrom.isValid() || !utcTo.isValid()) { return initialPeriod; }

        // If the converted UTC 'to' date is in the future (UTC), move it
        // back one UTC day to avoid using an incomplete/invalid future day.
        let adjustedUtcTo = utcTo.clone();
        let adjustedUtcFrom = utcFrom.clone().add(1, 'day');
        if (adjustedUtcTo.isAfter(utcNow, 'days')) {
            adjustedUtcFrom = utcFrom.clone();
            adjustedUtcTo = utcNow.clone();
        }

        return {
            from: adjustedUtcFrom.format(PAYLOAD_DATE_FORMAT),
            to: adjustedUtcTo.format(PAYLOAD_DATE_FORMAT)
        };
    };

    const generateHandler = () => {
        const adjustedRange = checkFutureDate(reportingPeriod);
        if (fleetMode) {
            return generateFleetReport(selectedReportType, adjustedRange, selectedUnit).then(cleanup).catch(() => { });
        }
        if (fleetDetailedMode) {
            return generateFleetDetailedReport(selectedReportType, adjustedRange, selectedAssetsIdsForFleetDetailed, selectedUnit).then(cleanup).catch(() => { });
        }
        return generateReport(selectedReportType, selectedAsset, selectedUnit, selectedRawDataCollection, adjustedRange).then(cleanup).catch(() => { });
    };

    const onReportTypeChange = reportType => {
        setSelectedReportType(reportType);
        setReportingPeriod(initialPeriod);
    };

    const handleSelectReportingPeriod = (from, to) => {
        setReportingPeriod({ from, to });
    };

    const filterAssetOptions = () => {
        if (selectedReportType.reportTypeId === reportTypes.InstantAssetMotor) {
            return assetOptions.filter((option) => option.isMonitored);
        }
        return assetOptions;
    };

    const handleSelectedAssetCountForFleetDetailed = (count) => {
        if (count > 0) {
            setSelectedAssetCountForFleetDetailed(`${count} ${translate('ABB.Powertrain.Frontend.selected')}`);
        } else {
            setSelectedAssetCountForFleetDetailed('');
        }
    };

    const handleClearSelectedAsset = () => {
        setSelectedAssetsIdsForFleetDetailed([]);
        setSelectedAssetCountForFleetDetailed('');
    };

    return (
        <GeneralDialog
            show={isModalOpen}
            close={closeGeneratorModal}
            title={<span><strong>{translate('ABB.Powertrain.Frontend.reportsNewReportLabel')}</strong></span>}
            closeButton={true}
            dialogClassName='report__generator'
            cancelButtonProps={{
                text: translate('ABB.Powertrain.Frontend.cancelLabel'),
                disabled: reportGenerationLoadingStatus === apiCallStatus.LOADING
            }}
            acceptButtonProps={{
                disabled: !acceptButtonEnabled || !!generationError || reportGenerationLoadingStatus === apiCallStatus.LOADING,
                text: translate('ABB.Powertrain.Frontend.reportsSubmitLabel'),
                onClick: generateHandler
            }}
        >
            <Container fluid className='gx-0'>
                <LoadingStatusWrapper
                    loadingStatus={[...capabilitiesLoadingStatuses]}
                    useCustomAnchor
                    transparent >
                    <LoadingStatusWrapper
                        loadingStatus={reportGenerationLoadingStatus}
                        transparent
                        useCustomAnchor>
                        <div className='report__generator__modal'>
                            {generationError ?
                                <ErrorView
                                    errorCode={generationError.code}
                                    errorMessage={generationError.message}
                                    onRetry={() => setGenerationError(null)}
                                    onReportIssue={() => { }}
                                />
                                :
                                <form role='form'>
                                    <div className='report__generator__select'>

                                        <label htmlFor='report-generate-type-select'>{translate('ABB.Powertrain.Frontend.reportsReportType')}</label>
                                        <GeneralSelect
                                            options={availableReportTypes}
                                            getOptionLabel={option => translate(option.reportTypeLabel)}
                                            getOptionValue={option => option.reportTypeId}
                                            defaultValue={selectedReportType}
                                            value={selectedReportType}
                                            menuShouldScrollIntoView={false}
                                            onChange={onReportTypeChange}
                                            isSearchable={false}
                                            inputId='report-generate-type-select'
                                            name='report-generate-type-select'
                                            isDisabled={!!preselectedAsset}
                                        />
                                    </div>
                                    {
                                        fleetDetailedMode ?
                                            <>
                                                <div className='report__generator__select'>
                                                    <label htmlFor='report-generate-asset-input'>{translate('ABB.Powertrain.Frontend.reportsGenerateSelectedAssetsLabel')}</label>
                                                    <GeneralInput
                                                        className='report__generator__select-asset-input'
                                                        placeholder={translate('ABB.Powertrain.Frontend.select')}
                                                        value={selectedAssetCountForFleetDetailed}
                                                        id='report-generate-asset-input'
                                                        name='report-generate-asset-input'
                                                        disabled={true}
                                                    />
                                                    {
                                                        selectedAssetCountForFleetDetailed &&
                                                        <div className='report__generator__select-asset-close'>
                                                            <button className='report__generator__select-asset-close-button' onClick={() => handleClearSelectedAsset()}>
                                                                <IconClose />
                                                            </button>
                                                        </div>
                                                    }
                                                    <div className='report__generator__select-asset-menu'>
                                                        <button onClick={() => setIsShowSelectedAssetsDialog(true)} type='button'>
                                                            {/* already this icon menu added <IconMetalBallmenu /> will replace this <IconMetalBallmenu /> component */}
                                                            <svg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 16 16' fill='none'>
                                                                <path d='M4 6V10H0V6H4ZM10 6V10H6V6H10ZM16 6V10H12V6H16Z' fill='#1F1F1F' />
                                                            </svg>
                                                        </button>
                                                    </div>
                                                </div>
                                            </> : null
                                    }
                                    {
                                        !fleetMode && !fleetDetailedMode ?
                                            <div className='report__generator__select'>
                                                <label htmlFor='report-generate-asset-select'>{translate('ABB.Powertrain.Frontend.reportsGenerateAssetLabel')}</label>
                                                <GeneralSelect
                                                    options={filterAssetOptions()}
                                                    getOptionLabel={option => option.name || option.id}
                                                    getOptionValue={option => option.id}
                                                    menuShouldScrollIntoView={false}
                                                    onChange={asset => setSelectedAsset(asset)}
                                                    defaultValue={selectedAsset}
                                                    value={selectedAsset}
                                                    inputId='report-generate-asset-select'
                                                    name='report-generate-asset-select'
                                                    isDisabled={!!preselectedAsset}
                                                />
                                            </div> : null
                                    }
                                    <div className='report__generator__select'>
                                        <label htmlFor='report-generate-unit-select'>{translate('ABB.Powertrain.Frontend.reportGenerationUnitSelectionLabel')}</label>
                                        <GeneralSelect
                                            options={unitTypesForReport}
                                            getOptionLabel={option => translate(option.label)}
                                            getOptionValue={option => option.value}
                                            menuShouldScrollIntoView={false}
                                            onChange={unit => setSelectedUnit(unit)}
                                            value={selectedUnit}
                                            inputId='report-generate-unit-select'
                                            name='report-generate-unit-select'
                                            isDisabled={isMetricOnly}
                                        />
                                    </div>

                                    {selectedReportType?.reportTypeId === reportTypes.InstantAssetMotor &&
                                        <>
                                            <div className='report__generator__raw_data_collection'>
                                                <label>{translate('ABB.Powertrain.Frontend.reportGenerationRawDataCollection')}</label>
                                                <button
                                                    className={`raw_data_collection_content ${!selectedAsset ? 'disabled' : ''}`}
                                                    onClick={() => setShowRawDataCollectionDialog(true)}
                                                    type='button'>
                                                    {selectedRawDataCollection ?
                                                        <span>{formatDateTime(selectedRawDataCollection)}</span>
                                                        : <span className='raw_data_collection_placeholder'>{translate('ABB.Powertrain.Frontend.select')}</span>
                                                    }
                                                    <IconEllipsis width={11} height={24} viewBox={'0 0 11 24'} />
                                                </button>
                                            </div>
                                            <div className='rawdata30-days-note'>{translate('ABB.Powertrain.Frontend.rawDataCollectionMsgHint')}</div></>
                                    }

                                    <div className={`report__generator__date${dateRangeValidation.isValid ? '' : ' report__generator__date--invalid'}`}>
                                        <label>{translate('ABB.Powertrain.Frontend.reportsReportingPeriod')}</label>
                                        <TimeSelectorRow
                                            fromDate={reportingPeriod.from}
                                            toDate={reportingPeriod.to}
                                            handleSelectDate={handleSelectReportingPeriod}
                                            numberOfMonths={numberOfMonthsInView}
                                            maxDaysBack={maximumDaysRange}
                                            maxDaysRange={maximumDaysRange}
                                            minimumRequiredDaysRange={minimumRequiredDaysRange}
                                            footerIntervalText={translate('ABB.Powertrain.Frontend.reportDateRangeHelperText')}
                                        />
                                        {reportingPeriod.from && reportingPeriod.to &&
                                            <div className='report__generator__reporting-period'>
                                                {`${getNrOfDaysBetween(new Date(reportingPeriod.from), new Date(reportingPeriod.to))} ${translate('ABB.Powertrain.Frontend.datePickerDaysSelectedLbl')}`}
                                            </div>
                                        }
                                        {dateRangeValidation.error === DATE_RANGE_ERROR_TOO_SHORT &&
                                            <div className='report__generator__date-validation-error'>
                                                {translate('ABB.Powertrain.Frontend.reportDateRangeMinError')}
                                            </div>
                                        }
                                        {dateRangeValidation.error === DATE_RANGE_ERROR_TOO_LONG &&
                                            <div className='report__generator__date-validation-error'>
                                                {translate('ABB.Powertrain.Frontend.reportDateRangeMaxError')}
                                            </div>
                                        }
                                        {dateRangeValidation.error === DATE_RANGE_ERROR_OUTSIDE_RAW_DATA &&
                                            <div className='report__generator__date-validation-error'>
                                                {translate('ABB.Powertrain.Frontend.reportDateRangeOutsideRawDataError')}
                                            </div>
                                        }
                                    </div>

                                    <ReportGeneratorNotification selectedReportType={selectedReportType} />
                                </form>
                            }
                        </div>
                    </LoadingStatusWrapper>
                    {isShowRawDataCollectionDialog &&
                        <RawDataCollectionDialog
                            isShowRawDataCollectionDialog={isShowRawDataCollectionDialog}
                            setShowRawDataCollectionDialog={setShowRawDataCollectionDialog}
                            setSelectedRawDataCollection={setSelectedRawDataCollection}
                            getRawDataCollectionTimestamps={getRawDataCollectionTimestamps}
                            selectedAsset={selectedAsset}
                            rawDataCollections={rawDataCollections}
                            selectedTimestamp={selectedTimestamp}
                            setSelectedTimestamp={setSelectedTimestamp}
                            unitOfMeasurement={selectedUnit?.value}
                        />
                    }
                    {isShowSelectedAssetsDialog &&
                        <AssetsSelectionDialog
                            selectedReportType={selectedReportType}
                            isShowSelectedAssetsDialog={isShowSelectedAssetsDialog}
                            setIsShowSelectedAssetsDialog={setIsShowSelectedAssetsDialog}
                            setSelectedAssetsIdsForFleetDetailed={setSelectedAssetsIdsForFleetDetailed}
                            selectedAssetsIdsForFleetDetailed={selectedAssetsIdsForFleetDetailed}
                            filteredAssets={filterAssetOptions()}
                            onSelectedAssetCountChange={handleSelectedAssetCountForFleetDetailed}
                        />
                    }
                </LoadingStatusWrapper>
            </Container>
        </GeneralDialog>
    );
};

ReportGeneratorComponent.propTypes = {
    model: PropTypes.shape({
        isModalOpen: PropTypes.bool,
        capabilitiesLoadingStatuses: PropTypes.array,
        assetOptions: PropTypes.array,
        preselectedAsset: PropTypes.object,
    }),
    rawDataCollections: PropTypes.object,
    actions: PropTypes.shape({
        setReportTypeInRedux: PropTypes.func,
        // getCapabilitiesByDataSourceType: PropTypes.func,
        generateReport: PropTypes.func,
        generateFleetReport: PropTypes.func,
        closeGeneratorModal: PropTypes.func,
        getSensorOfAssets: PropTypes.func,
        getRawDataCollectionTimestamps: PropTypes.func
    })
};

